import React, { createContext, useContext, useState, useEffect } from 'react';
import { NextIntlClientProvider } from 'next-intl';
import { detectBrowserLocale, saveLocalePreference, isRTL } from '../lib/locale';
import { locales, defaultLocale } from '../i18n';
import Loading from '../components/ui/loading';

const LocaleContext = createContext();

export const LocaleProvider = ({ children, initialLocale }) => {
  const [locale, setLocale] = useState(initialLocale || defaultLocale);
  const [messages, setMessages] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Load messages for the current locale
  const loadMessages = async (localeCode) => {
    try {
      const loadedMessages = (await import(`../messages/${localeCode}.json`)).default;
      setMessages(loadedMessages);
    } catch (error) {
      console.error(`Failed to load messages for locale: ${localeCode}`, error);
      // Fallback to default locale
      if (localeCode !== defaultLocale) {
        const fallbackMessages = (await import(`../messages/${defaultLocale}.json`)).default;
        setMessages(fallbackMessages);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Detect and set locale on mount
  useEffect(() => {
    const detectedLocale = detectBrowserLocale();
    setLocale(detectedLocale);
    loadMessages(detectedLocale);

    // Set HTML dir attribute for RTL languages
    if (typeof document !== 'undefined') {
      document.documentElement.dir = isRTL(detectedLocale) ? 'rtl' : 'ltr';
      document.documentElement.lang = detectedLocale;
    }
  }, []);

  // Change locale function
  const changeLocale = async (newLocale) => {
    if (!locales.includes(newLocale)) {
      console.warn(`Locale ${newLocale} is not supported`);
      return;
    }

    setIsLoading(true);
    setLocale(newLocale);
    saveLocalePreference(newLocale);
    await loadMessages(newLocale);

    // Update HTML attributes
    if (typeof document !== 'undefined') {
      document.documentElement.dir = isRTL(newLocale) ? 'rtl' : 'ltr';
      document.documentElement.lang = newLocale;
    }
  };

  const contextValue = {
    locale,
    changeLocale,
    isLoading,
    isRTL: isRTL(locale),
  };

  if (isLoading || !messages) {
    return <Loading/>
  }

  return (
    <LocaleContext.Provider value={contextValue}>
      <NextIntlClientProvider locale={locale} messages={messages}>
        {children}
      </NextIntlClientProvider>
    </LocaleContext.Provider>
  );
};

// Custom hook to use locale context
export const useLocale = () => {
  const context = useContext(LocaleContext);
  if (!context) {
    throw new Error('useLocale must be used within a LocaleProvider');
  }
  return context;
};
